import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { fetchCDBalanceList } from "@actions/fetchCDBalanceAction";
import { usePagination } from "@components/index";
import { useAppConfig } from "@context/AppConfigContext";
import {
  submitAmountData,
  verifyOtpAddCdBalance,
} from "@actions/amountSubmissionDataAction";
import { validateAmount } from "@utils/index";
import { useCSVExport } from "@hooks/useCSVExport";
import {
  formatDate,
  formatDateToYYYYMMDD,
  isValidDateRange,
} from "@utils/dateUtils";
import { MIS_FIELDS_CDBALANCE } from "@constants/misDownloadData";

export const CDBALANCE_STATUS = [
  { value: "Credit", label: "Credit" },
  { value: "Debit", label: "Debit" },
];

// Error messages
export const errorMessages = {
  selectPartner: "Please select a partner",
  enterValidAmount: "Please enter a valid amount",
  numbersOnly: "Only numbers are allowed",
};

// API service function to fetch CD Balance data
export const fetchCDBalanceData = async (client, txnType) => {
  try {
    // Build parameters object
    let params = {
      clientName: client?.clientName,
      txn_type: txnType,
    };
    const response = await fetchCDBalanceList(params);

    // Check if response is successful
    if (response?.status) {
      return {
        success: true,
        data: response.balanceHistory || response.data || [],
        currentBalance: response.currentCdBalance || 0,
        totalTransactions: response.no_of_transactions || 0,
        error: null,
      };
    } else {
      return {
        success: false,
        data: [],
        currentBalance: 0,
        totalTransactions: 0,
        error: response?.message || "Failed to fetch CD Balance data",
      };
    }
  } catch (err) {
    return {
      success: false,
      data: [],
      currentBalance: 0,
      totalTransactions: 0,
      error: "Failed to fetch CD Balance data",
    };
  }
};

// API service function to submit amount data
export const getSubmissionData = async (requestData) => {
  try {
    const response = await submitAmountData(requestData);
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to submit data",
      data: null,
    };
  }
};

// API service function to verify OTP to add Balance
export const verifyOtp = async (requestData) => {
  try {
    const response = await verifyOtpAddCdBalance(requestData);
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to submit data",
      data: null,
    };
  }
};

// Data mapper for MIS export
const mapCDBalanceToMISData = (balance, index) => ({
  "SR. No ": index + 1,
  "Updated By": balance.user_name || "-",
  "Txn Type": balance.txn_type || "-",
  Mode: balance.mode || "-",
  Date: formatDate(balance.txn_date) || "-",
  Status: balance.txn_status || "-",
  "Updated Amount": balance.amount || "-",
  "Txn Amount": balance.amount_difference || "-",
});

/**
 * Custom hook for CD Balance management
 */
export const useCDBalanceData = () => {
  const [balanceAmount, setBalanceAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cdBalances, setCDBalances] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [otpDetails, setOtpDetails] = useState({
    showOtpDrawer: false,
    otpValue: "",
    otpError: "",
    otpSubmitLoading: false,
  });
  const [otpReqId, setOtpReqId] = useState("");

  const { user, selectedClient } = useAppConfig();
  const isInitialMount = useRef(true);
  const { exportToXLSX, generateDateFilename } = useCSVExport();

  // Handle amount input change with validation
  const handleAmountChange = (e) => {
    const value = e.target.value;

    // Check value length
    if (value.length > 10) {
      setError("Amount cannot exceed 10 digits");
      return;
    }

    // Validate amount format
    if (validateAmount(value)) {
      setBalanceAmount(value);
      setError("");
    } else {
      setError(errorMessages.numbersOnly);
    }
  };

  // Load CD Balance data
  const loadCDBalanceData = useCallback(async (params) => {
    setLoading(true);

    try {
      const response = await fetchCDBalanceList(params);

      if (response?.status) {
        setCDBalances(response.balanceHistory || response.data || []);
        setCurrentBalance(response.currentCdBalance || 0);
        setError(null);
      } else {
        setCDBalances([]);
        setCurrentBalance(0);
        setError(response?.message || "Failed to fetch CD Balance data");
      }
    } catch (err) {
      setCDBalances([]);
      setCurrentBalance(0);
      setError("Failed to fetch CD Balance data");
    }

    setLoading(false);
  }, []);

  // Initial data load and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      const params = {
        clientName: selectedClient?.clientName || "emt",
        startDate: formatDateToYYYYMMDD(new Date()),
        endDate: formatDateToYYYYMMDD(new Date()),
      };

      isInitialMount.current = false;
      loadCDBalanceData(params);
    } else {
      if (
        (selectedStatusFilter || isValidDateRange(fromDate, toDate, false)) &&
        !isInitialMount.current
      ) {
        let params = {
          clientName: selectedClient?.clientName || "emt",
        };

        if (selectedStatusFilter) {
          params.txn_type = selectedStatusFilter;
        }

        if (isValidDateRange(fromDate, toDate, false)) {
          params.startDate = formatDateToYYYYMMDD(fromDate);
          params.endDate = formatDateToYYYYMMDD(toDate);
        }

        loadCDBalanceData(params);
      }
    }
  }, [
    fromDate,
    toDate,
    selectedStatusFilter,
    selectedClient,
    loadCDBalanceData,
  ]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((status) => {
    setSelectedStatusFilter(status);
  }, []);

  // Handle date range change
  const handleDateChange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle MIS data download using the XLSX export hook
  const handleDownloadMIS = useCallback(() => {
    const filename = generateDateFilename("CD_Balance_Data");
    exportToXLSX(
      cdBalances,
      MIS_FIELDS_CDBALANCE,
      filename,
      mapCDBalanceToMISData,
    );
  }, [cdBalances, exportToXLSX, generateDateFilename]);

  // Clear all filters
  const handleClearFilters = () => {
    setFromDate(new Date());
    setToDate(new Date());
    setSelectedStatusFilter("");
  };

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedCDBalances,
    handlePageChange,
  } = usePagination(cdBalances, 10);

  // Handle form submission
  const handleSubmit = async () => {
    const userName = user.user.name;
    const userEmail = user.user.email;
    const userId = String(user.user.id);

    if (!selectedClient) {
      setError(errorMessages.selectPartner);
      return false;
    }

    if (!balanceAmount || balanceAmount === "0") {
      setError(errorMessages.enterValidAmount);
      return false;
    }

    const requestData = {
      clientName: selectedClient?.clientName,
      amount: balanceAmount,
      user_Name: userName,
      user_Email: userEmail,
      user_Id: userId,
      Type: "Claim",
    };

    const submissionData = await getSubmissionData(requestData);

    if (submissionData.success) {
      setOtpReqId(submissionData?.data?.requestId);
      setError("");
      toast.success("OTP sent to your email");
      setOtpDetails((prev) => ({ ...prev, showOtpDrawer: true }));
    }

    return true;
  };

  const handleOtpChange = (value) => {
    setOtpDetails((prev) => ({ ...prev, otpValue: value, otpError: "" }));
  };

  const handleCloseOtpModal = () => {
    setBalanceAmount("");
    setOtpDetails({
      showOtpDrawer: false,
      otpValue: "",
      otpError: "",
      otpSubmitLoading: false,
    });
  };

  const handleOtpSubmit = async () => {
    const reqParam = {
      otp: otpDetails.otpValue,
      requestId: otpReqId,
    };

    const response = await verifyOtp(reqParam);

    if (response?.success) {
      handleCloseOtpModal();
      setBalanceAmount("");

      toast.success(response?.data?.message || "Balance updated Successfully");

      const params = {
        clientName: selectedClient?.clientName || "EMT",
        startDate: formatDateToYYYYMMDD(new Date()),
        endDate: formatDateToYYYYMMDD(new Date()),
      };

      loadCDBalanceData(params);
    } else {
      setOtpDetails((prev) => ({
        ...prev,
        otpError: response?.error || "Invalid OTP",
      }));
    }
  };

  const handleResendOTP = () => {
    handleSubmit();
  };

  return {
    // State
    balanceAmount,
    error,
    loading,
    currentBalance, // Current CD Balance from API
    fromDate,
    toDate,
    handleDateChange,

    // Handlers
    handleAmountChange,
    handleClearFilters,
    handleSubmit,
    handleDownloadMIS,
    // Data
    cdBalances: paginatedCDBalances, // Paginated CD Balances for display
    allCDBalances: cdBalances, // All filtered CD Balances for pagination component
    currentPage,
    handlePageChange,
    handleStatusFilterChange,
    selectedStatusFilter,
    otpDetails,
    handleOtpChange,
    handleCloseOtpModal,
    handleOtpSubmit,
    handleResendOTP,
  };
};
